/**
 * Pure drag-to-dismiss maths for `Sheet.tsx` — kept out of the component so it
 * is testable without mounting framer-motion's drag gesture machinery.
 *
 * `Sheet` drags the panel on `y` with `dragConstraints={{ top: 0, bottom: 0 }}`
 * and `dragElastic={{ top: 0, bottom: 1 }}`: the top is pinned (no rubber-band
 * upward) and the bottom moves 1:1 with the pointer (no resistance), which is
 * the standard bottom-sheet drag feel. On release, if `shouldDismissSheet`
 * says no, framer's own constraint enforcement animates the panel back to 0 —
 * nothing else has to do that by hand.
 */

export interface SheetDragEnd {
  /** Vertical drag offset from the panel's rest position, in px. Positive =
   * dragged down. */
  offsetY: number;
  /** Vertical drag velocity at release, in px/s. Positive = moving down. */
  velocityY: number;
}

/** Dragged past this many px down dismisses, regardless of velocity. */
export const SHEET_DISMISS_DISTANCE = 120;

/** A downward flick at or above this speed dismisses even with a small
 * offset — "flick closes it," per the brief. */
export const SHEET_DISMISS_VELOCITY = 700;

export function shouldDismissSheet(
  drag: SheetDragEnd,
  dismissDistance: number = SHEET_DISMISS_DISTANCE,
): boolean {
  if (drag.offsetY <= 0) return false; // dragged up (or not at all) — never dismisses
  if (drag.velocityY >= SHEET_DISMISS_VELOCITY) return true;
  return drag.offsetY > dismissDistance;
}

/**
 * Backdrop opacity for the CURRENT drag offset — fades proportionally as the
 * sheet is pulled down, floored at 0.25 so the backdrop never fully
 * disappears before the sheet actually closes.
 */
export function backdropOpacityForDrag(
  offsetY: number,
  dismissDistance: number = SHEET_DISMISS_DISTANCE,
): number {
  if (offsetY <= 0) return 1;
  const progress = Math.min(offsetY / (dismissDistance * 1.6), 1);
  return 1 - progress * 0.75;
}
